import { EmailConversationChannelBinding, EmailDeliveryDestination } from "./email.ts";
import { CommunicationChannelBinding, CommunicationLoopDelivery } from "./communication.ts";
import { WorkBrief, WorkVerification } from "./work.ts";
import { LearningMetadata, LearningStatus } from "./learning.ts";
/**
 * Stable product language for the business-operator workspace.
 *
 * Maito projects agent execution, runs, approvals, and workspace state into
 * these shapes. Native identifiers never become product primary keys and no
 * repository/worktree fields belong in this module.
 */
import * as Schema from "effect/Schema";
import * as DateTime from "effect/DateTime";
import * as Option from "effect/Option";

import {
  ActivityId,
  CommandId,
  ConversationId,
  HarnessConnectionId,
  IsoDateTime,
  NonNegativeInt,
  OperatorApprovalId,
  OperatorProjectId,
  PositiveInt,
  RunId,
  TrimmedNonEmptyString,
  WorkspaceFactId,
} from "./baseSchemas.ts";
import {
  ChatAttachment,
  ChatFileAttachment,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  UploadChatAttachment,
} from "./orchestration.ts";
import { SlackConversationChannelBinding } from "./slack.ts";

const OPEN_SLUG_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const OpenSlug = TrimmedNonEmptyString.check(
  Schema.isMaxLength(64),
  Schema.isPattern(OPEN_SLUG_PATTERN),
);

export const ExternalRef = Schema.Struct({
  connectionId: HarnessConnectionId,
  nativeId: TrimmedNonEmptyString,
  nativeVersion: Schema.optionalKey(TrimmedNonEmptyString),
});
export type ExternalRef = typeof ExternalRef.Type;

/** Stable, server-derived identity used only for operator visibility decisions. */
export const OperatorPrincipalId = TrimmedNonEmptyString;
export type OperatorPrincipalId = typeof OperatorPrincipalId.Type;

export const ConversationMember = Schema.Struct({
  principalId: OperatorPrincipalId.check(Schema.isPattern(/^account:[A-Za-z0-9_-]{1,128}$/)),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
});
export type ConversationMember = typeof ConversationMember.Type;
const ConversationMembers = Schema.Array(ConversationMember);

/**
 * Conversations are private unless a workspace-owned workflow explicitly creates them.
 * The optional field on Conversation is a wire-compatibility allowance for pre-multiplayer
 * projections; servers treat an absent audience as private legacy-owner data.
 */
export const ConversationAudience = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("private"),
    ownerId: OperatorPrincipalId,
    slackParticipants: Schema.optionalKey(
      Schema.Struct({
        teamId: TrimmedNonEmptyString,
        userIds: Schema.Array(TrimmedNonEmptyString),
      }),
    ),
    members: Schema.optionalKey(
      Schema.Array(
        Schema.Struct({
          ...ConversationMember.fields,
          invitationId: Schema.optionalKey(CommandId),
        }),
      ),
    ),
  }),
  Schema.Struct({ type: Schema.Literal("workspace") }),
]);
export type ConversationAudience = typeof ConversationAudience.Type;

export const HarnessConnectionMode = Schema.Literals(["local", "remote"]);
export type HarnessConnectionMode = typeof HarnessConnectionMode.Type;
export const HarnessConnectionStatus = Schema.Literals([
  "connecting",
  "connected",
  "disconnected",
  "unavailable",
  "failed",
]);
export type HarnessConnectionStatus = typeof HarnessConnectionStatus.Type;

export const HarnessConnection = Schema.Struct({
  id: HarnessConnectionId,
  setupId: Schema.optionalKey(Schema.String),
  displayName: TrimmedNonEmptyString,
  mode: HarnessConnectionMode,
  status: HarnessConnectionStatus,
  authenticated: Schema.Boolean,
  version: Schema.optionalKey(TrimmedNonEmptyString),
  endpointLabel: Schema.optionalKey(TrimmedNonEmptyString),
  lastError: Schema.optionalKey(Schema.String),
  statusChangedAt: Schema.optionalKey(IsoDateTime),
  observedAt: IsoDateTime,
});
export type HarnessConnection = typeof HarnessConnection.Type;

const ConnectionDetails = {
  displayName: Schema.optionalKey(TrimmedNonEmptyString),
  visibility: Schema.optionalKey(Schema.Literals(["private", "workspace"])),
};

export const OperatorConnection = Schema.Struct({
  ...ConnectionDetails,
  integrationId: Schema.optionalKey(TrimmedNonEmptyString),
  ownerId: Schema.optionalKey(OperatorPrincipalId),
  canManage: Schema.optionalKey(Schema.Boolean),
  canChangeVisibility: Schema.optionalKey(Schema.Boolean),
  source: Schema.optionalKey(Schema.Literals(["custom", "integration"])),
  name: TrimmedNonEmptyString,
  transport: Schema.Literals(["http", "stdio", "unknown"]),
  command: Schema.optionalKey(Schema.String),
  auth: Schema.optionalKey(
    Schema.Literals(["none", "oauth", "header", "basic", "client_credentials", "private_key_jwt"]),
  ),
  enabled: Schema.Boolean,
  tools: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  allowedTools: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  status: Schema.optionalKey(Schema.Literals(["configured", "needs_auth", "connected", "error"])),
  error: Schema.optionalKey(Schema.String),
});
export type OperatorConnection = typeof OperatorConnection.Type;

export const OperatorConnectionsSnapshot = Schema.Struct({
  principalId: Schema.optionalKey(OperatorPrincipalId),
  canCreatePrivate: Schema.optionalKey(Schema.Boolean),
  connections: Schema.Array(OperatorConnection),
});
export type OperatorConnectionsSnapshot = typeof OperatorConnectionsSnapshot.Type;

export const OperatorConnectionTool = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.String,
  title: Schema.optionalKey(Schema.String),
  readOnly: Schema.optionalKey(Schema.Boolean),
});
export type OperatorConnectionTool = typeof OperatorConnectionTool.Type;

export const OperatorConnectionTestResult = Schema.Struct({
  ok: Schema.Boolean,
  error: Schema.optionalKey(Schema.String),
  tools: Schema.Array(OperatorConnectionTool),
});
export type OperatorConnectionTestResult = typeof OperatorConnectionTestResult.Type;

export const OperatorConnectionOAuth = Schema.Struct({
  flowId: TrimmedNonEmptyString,
  status: TrimmedNonEmptyString,
  authorizationUrl: Schema.optionalKey(Schema.String),
  error: Schema.optionalKey(Schema.String),
});
export type OperatorConnectionOAuth = typeof OperatorConnectionOAuth.Type;

const OperatorConnectionActionScope = {
  projectId: OperatorProjectId,
};

export const OperatorConnectionOAuthClient = Schema.Struct({
  clientId: TrimmedNonEmptyString,
  clientSecret: Schema.optionalKey(TrimmedNonEmptyString),
  privateKey: Schema.optionalKey(TrimmedNonEmptyString),
  algorithm: Schema.optionalKey(Schema.Literals(["RS256", "ES256", "PS256"])),
  scope: Schema.optionalKey(Schema.String),
});
export type OperatorConnectionOAuthClient = typeof OperatorConnectionOAuthClient.Type;

const StdioConnectionConfiguration = Schema.Struct({
  transport: Schema.Literal("stdio"),
  command: TrimmedNonEmptyString,
  args: Schema.Array(Schema.String),
  env: Schema.Record(TrimmedNonEmptyString, Schema.NullOr(Schema.String)),
});
const HttpConnectionConfiguration = Schema.Struct({
  transport: Schema.Literal("http"),
  httpTransport: Schema.optionalKey(Schema.Literals(["streamable", "sse"])),
  url: TrimmedNonEmptyString,
  auth: Schema.Literals([
    "none",
    "oauth",
    "header",
    "basic",
    "client_credentials",
    "private_key_jwt",
  ]),
  bearerToken: Schema.optionalKey(TrimmedNonEmptyString),
  headers: Schema.optionalKey(Schema.Record(TrimmedNonEmptyString, Schema.NullOr(Schema.String))),
  basicUsername: Schema.optionalKey(TrimmedNonEmptyString),
  basicPassword: Schema.optionalKey(Schema.String),
  oauthClient: Schema.optionalKey(Schema.NullOr(OperatorConnectionOAuthClient)),
});
export const OperatorConnectionConfiguration = Schema.Union([
  StdioConnectionConfiguration,
  HttpConnectionConfiguration,
]);
export type OperatorConnectionConfiguration = typeof OperatorConnectionConfiguration.Type;

/** Agent-prepared setup contains credential names, never their values. */
export const OperatorConnectionDraft = Schema.Union([
  Schema.Struct({
    transport: StdioConnectionConfiguration.fields.transport,
    command: StdioConnectionConfiguration.fields.command,
    args: StdioConnectionConfiguration.fields.args,
    envKeys: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  }),
  Schema.Struct({
    transport: HttpConnectionConfiguration.fields.transport,
    httpTransport: HttpConnectionConfiguration.fields.httpTransport,
    url: HttpConnectionConfiguration.fields.url,
    auth: HttpConnectionConfiguration.fields.auth,
    headerKeys: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
    basicUsername: HttpConnectionConfiguration.fields.basicUsername,
    oauthClient: Schema.optionalKey(
      Schema.Struct({
        clientId: OperatorConnectionOAuthClient.fields.clientId,
        algorithm: OperatorConnectionOAuthClient.fields.algorithm,
        scope: OperatorConnectionOAuthClient.fields.scope,
      }),
    ),
  }),
]);
export type OperatorConnectionDraft = typeof OperatorConnectionDraft.Type;

export const OperatorConnectionActionInput = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("readConversationRequest"),
    ...OperatorConnectionActionScope,
    requestId: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("authorizeConversationRequest"),
    ...OperatorConnectionActionScope,
    requestId: TrimmedNonEmptyString,
    decision: Schema.Literals(["connect", "cancel"]),
    token: Schema.optionalKey(TrimmedNonEmptyString),
    name: Schema.optionalKey(TrimmedNonEmptyString),
    displayName: Schema.optionalKey(TrimmedNonEmptyString),
    visibility: Schema.optionalKey(Schema.Literals(["private", "workspace"])),
    configuration: Schema.optionalKey(OperatorConnectionConfiguration),
  }),
  Schema.Struct({
    action: Schema.Literal("setDetails"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
    displayName: TrimmedNonEmptyString,
    visibility: Schema.Literals(["private", "workspace"]),
  }),
  Schema.Struct({
    action: Schema.Literal("setTools"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
    tools: Schema.Array(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    action: Schema.Literal("probe"),
    ...OperatorConnectionActionScope,
    url: TrimmedNonEmptyString,
    httpTransport: Schema.optionalKey(Schema.Literals(["streamable", "sse"])),
  }),
  Schema.Struct({
    action: Schema.Literal("read"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("save"),
    ...OperatorConnectionActionScope,
    ...ConnectionDetails,
    replace: Schema.optionalKey(Schema.Boolean),
    allowedTools: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
    ...StdioConnectionConfiguration.fields,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("save"),
    ...OperatorConnectionActionScope,
    ...ConnectionDetails,
    replace: Schema.optionalKey(Schema.Boolean),
    allowedTools: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
    source: Schema.optionalKey(Schema.Literals(["custom", "integration"])),
    integrationId: Schema.optionalKey(TrimmedNonEmptyString),
    ...HttpConnectionConfiguration.fields,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("test"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("setEnabled"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
    enabled: Schema.Boolean,
  }),
  Schema.Struct({
    action: Schema.Literal("remove"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("authenticate"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("oauthStatus"),
    ...OperatorConnectionActionScope,
    flowId: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("cancelOAuth"),
    ...OperatorConnectionActionScope,
    flowId: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("oauthCallback"),
    ...OperatorConnectionActionScope,
    name: TrimmedNonEmptyString,
    code: Schema.optionalKey(Schema.String),
    state: Schema.optionalKey(Schema.String),
    error: Schema.optionalKey(Schema.String),
    iss: Schema.optionalKey(Schema.String),
  }),
]);
export type OperatorConnectionActionInput = typeof OperatorConnectionActionInput.Type;

export const OperatorConnectionDetail = Schema.Struct({
  ...OperatorConnection.fields,
  url: Schema.optionalKey(Schema.String),
  httpTransport: Schema.optionalKey(Schema.Literals(["streamable", "sse"])),
  args: Schema.optionalKey(Schema.Array(Schema.String)),
  envKeys: Schema.optionalKey(Schema.Array(Schema.String)),
  hasBearerToken: Schema.Boolean,
  headerKeys: Schema.optionalKey(Schema.Array(Schema.String)),
  basicUsername: Schema.optionalKey(Schema.String),
  hasBasicPassword: Schema.optionalKey(Schema.Boolean),
  oauthClient: Schema.optionalKey(
    Schema.Struct({
      clientId: Schema.String,
      algorithm: Schema.optionalKey(Schema.Literals(["RS256", "ES256", "PS256"])),
      scope: Schema.optionalKey(Schema.String),
      hasClientSecret: Schema.Boolean,
      hasPrivateKey: Schema.Boolean,
    }),
  ),
});
export type OperatorConnectionDetail = typeof OperatorConnectionDetail.Type;
export const OperatorConnectionProbe = Schema.Struct({
  auth: Schema.Literals([
    "none",
    "oauth",
    "header",
    "basic",
    "client_credentials",
    "private_key_jwt",
  ]),
  supportsRegistration: Schema.optionalKey(Schema.Boolean),
  tools: OperatorConnectionTestResult.fields.tools,
});
export type OperatorConnectionProbe = typeof OperatorConnectionProbe.Type;
export const OperatorConversationConnection = Schema.Struct({
  principal: Schema.optionalKey(Schema.String),
  integrationId: Schema.optionalKey(Schema.String),
  connectionName: Schema.optionalKey(Schema.String),
  label: Schema.optionalKey(Schema.String),
  reason: Schema.optionalKey(Schema.String),
  auth: Schema.optionalKey(HttpConnectionConfiguration.fields.auth),
  configuration: Schema.optionalKey(OperatorConnectionDraft),
  visibility: Schema.optionalKey(Schema.Literals(["private", "workspace"])),
  status: Schema.Literals([
    "pending",
    "authorizing",
    "connected",
    "cancelled",
    "failed",
    "expired",
  ]),
  authorizationUrl: Schema.optionalKey(Schema.String),
});
export type OperatorConversationConnection = typeof OperatorConversationConnection.Type;

export const OperatorConnectionActionResult = Schema.Struct({
  conversationRequest: Schema.optionalKey(OperatorConversationConnection),
  connection: Schema.optionalKey(OperatorConnectionDetail),
  probe: Schema.optionalKey(OperatorConnectionProbe),
  snapshot: Schema.optionalKey(OperatorConnectionsSnapshot),
  test: Schema.optionalKey(OperatorConnectionTestResult),
  oauth: Schema.optionalKey(OperatorConnectionOAuth),
  callbackHtml: Schema.optionalKey(Schema.String),
});
export type OperatorConnectionActionResult = typeof OperatorConnectionActionResult.Type;

export const OperatorVaultSecretName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[A-Z][A-Z0-9_]*$/),
);
export type OperatorVaultSecretName = typeof OperatorVaultSecretName.Type;

export const OperatorVaultSecretValue = Schema.String.check(
  Schema.isMaxLength(65_536),
  Schema.makeFilter(
    (value) =>
      (value.trim().length > 0 &&
        !value.includes("\0") &&
        !value.includes("\r") &&
        !value.includes("\n")) ||
      "Secret values must be non-empty and single-line.",
  ),
);
export type OperatorVaultSecretValue = typeof OperatorVaultSecretValue.Type;

export const OperatorVaultSecret = Schema.Struct({
  name: OperatorVaultSecretName,
  preview: Schema.String,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OperatorVaultSecret = typeof OperatorVaultSecret.Type;

export const OperatorVaultSecretUpsertInput = Schema.Struct({
  name: OperatorVaultSecretName,
  value: OperatorVaultSecretValue,
});
export type OperatorVaultSecretUpsertInput = typeof OperatorVaultSecretUpsertInput.Type;

export const OperatorVaultSecretDeleteInput = Schema.Struct({
  name: OperatorVaultSecretName,
});
export type OperatorVaultSecretDeleteInput = typeof OperatorVaultSecretDeleteInput.Type;

export class OperatorVaultError extends Schema.TaggedErrorClass<OperatorVaultError>()(
  "OperatorVaultError",
  {
    operation: Schema.Literals(["initialize", "list", "upsert", "delete", "decrypt"]),
    reason: Schema.Literals(["invalid_name", "unavailable"]),
    message: Schema.String,
  },
) {}

export const OperatorProviderConfigurationKey = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[A-Z][A-Z0-9_]*$/),
);
export type OperatorProviderConfigurationKey = typeof OperatorProviderConfigurationKey.Type;

export const OperatorProviderConfigurationOption = Schema.Struct({
  key: OperatorProviderConfigurationKey,
  providerId: Schema.optionalKey(TrimmedNonEmptyString),
  providerLabel: TrimmedNonEmptyString,
  description: Schema.String,
  configured: Schema.Boolean,
  redactedValue: Schema.optionalKey(Schema.String),
  secret: Schema.Boolean,
  advanced: Schema.Boolean,
});
export type OperatorProviderConfigurationOption = typeof OperatorProviderConfigurationOption.Type;

export const OperatorProviderOAuthFlow = Schema.Literals(["device_code", "pkce", "external"]);
export type OperatorProviderOAuthFlow = typeof OperatorProviderOAuthFlow.Type;

export const OperatorProviderOAuth = Schema.Struct({
  flow: OperatorProviderOAuthFlow,
  connected: Schema.Boolean,
});
export type OperatorProviderOAuth = typeof OperatorProviderOAuth.Type;

export const OperatorProviderConfigurationProvider = Schema.Struct({
  id: OpenSlug,
  label: TrimmedNonEmptyString,
  description: Schema.String,
  authType: Schema.String,
  connected: Schema.Boolean,
  active: Schema.Boolean,
  models: Schema.Array(TrimmedNonEmptyString),
  capabilities: Schema.optionalKey(
    Schema.Record(
      Schema.String,
      Schema.Struct({
        reasoning: Schema.optionalKey(Schema.Boolean),
        canDisableReasoning: Schema.optionalKey(Schema.Boolean),
        supportedReasoningEfforts: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
      }),
    ),
  ),
  fields: Schema.Array(OperatorProviderConfigurationOption),
  oauth: Schema.optionalKey(OperatorProviderOAuth),
});
export type OperatorProviderConfigurationProvider =
  typeof OperatorProviderConfigurationProvider.Type;

export const OperatorProviderReasoningEffort = Schema.Literals([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
  "ultra",
]);
export type OperatorProviderReasoningEffort = typeof OperatorProviderReasoningEffort.Type;

export const OperatorProviderConfiguration = Schema.Struct({
  activeProviderId: Schema.optionalKey(OpenSlug),
  activeModel: Schema.optionalKey(TrimmedNonEmptyString),
  reasoningEffort: OperatorProviderReasoningEffort,
  providers: Schema.Array(OperatorProviderConfigurationProvider),
});
export type OperatorProviderConfiguration = typeof OperatorProviderConfiguration.Type;

/** The value is write-only and never appears in provider configuration reads. */
export const OperatorProviderConfigurationInput = Schema.Struct({
  projectId: Schema.optionalKey(OperatorProjectId),
  key: OperatorProviderConfigurationKey,
  value: TrimmedNonEmptyString,
});
export type OperatorProviderConfigurationInput = typeof OperatorProviderConfigurationInput.Type;

export const OperatorProviderActivationInput = Schema.Struct({
  purpose: Schema.optionalKey(Schema.Literal("coding")),
  projectId: Schema.optionalKey(OperatorProjectId),
  providerId: Schema.NullOr(OpenSlug),
  model: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OperatorProviderActivationInput = typeof OperatorProviderActivationInput.Type;

export const OperatorProviderReasoningEffortInput = Schema.Struct({
  purpose: Schema.optionalKey(Schema.Literal("coding")),
  projectId: Schema.optionalKey(OperatorProjectId),
  reasoningEffort: OperatorProviderReasoningEffort,
});
export type OperatorProviderReasoningEffortInput = typeof OperatorProviderReasoningEffortInput.Type;

export const OperatorProviderDisconnectInput = Schema.Struct({
  projectId: Schema.optionalKey(OperatorProjectId),
  providerId: OpenSlug,
});
export type OperatorProviderDisconnectInput = typeof OperatorProviderDisconnectInput.Type;

const OperatorProviderOAuthActionScope = {
  projectId: Schema.optionalKey(OperatorProjectId),
} as const;

export const OperatorProviderOAuthActionInput = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("start"),
    ...OperatorProviderOAuthActionScope,
    providerId: OpenSlug,
  }),
  Schema.Struct({
    action: Schema.Literal("poll"),
    ...OperatorProviderOAuthActionScope,
    providerId: OpenSlug,
    sessionId: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    action: Schema.Literal("submit"),
    ...OperatorProviderOAuthActionScope,
    providerId: OpenSlug,
    sessionId: TrimmedNonEmptyString,
    promptId: TrimmedNonEmptyString,
    code: Schema.String.check(Schema.isMaxLength(16_384)),
  }),
  Schema.Struct({
    action: Schema.Literal("cancel"),
    ...OperatorProviderOAuthActionScope,
    sessionId: TrimmedNonEmptyString,
  }),
]);
export type OperatorProviderOAuthActionInput = typeof OperatorProviderOAuthActionInput.Type;

export const OperatorProviderOAuthActionResult = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("prompt"),
    sessionId: TrimmedNonEmptyString,
    promptId: TrimmedNonEmptyString,
    type: Schema.Literals(["text", "secret", "select", "manual_code"]),
    message: Schema.String,
    placeholder: Schema.optionalKey(Schema.String),
    options: Schema.optionalKey(
      Schema.Array(Schema.Struct({ id: TrimmedNonEmptyString, label: Schema.String })),
    ),
    authorizationUrl: Schema.optionalKey(TrimmedNonEmptyString),
    instructions: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("device_code"),
    sessionId: TrimmedNonEmptyString,
    userCode: TrimmedNonEmptyString,
    verificationUrl: TrimmedNonEmptyString,
    expiresIn: Schema.Number,
    pollInterval: Schema.Number,
  }),
  Schema.Struct({
    kind: Schema.Literal("pkce"),
    sessionId: TrimmedNonEmptyString,
    authorizationUrl: TrimmedNonEmptyString,
    expiresIn: Schema.Number,
    instructions: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("status"),
    status: TrimmedNonEmptyString,
    errorMessage: Schema.optionalKey(Schema.String),
    expiresAt: Schema.optionalKey(Schema.Number),
  }),
  Schema.Struct({
    kind: Schema.Literal("cancelled"),
    ok: Schema.Boolean,
  }),
]);
export type OperatorProviderOAuthActionResult = typeof OperatorProviderOAuthActionResult.Type;

const OperatorAutomationTriggerId = TrimmedNonEmptyString;
const OperatorAutomationScheduleTriggerFields = {
  id: OperatorAutomationTriggerId,
  kind: Schema.Literal("schedule"),
  schedule: TrimmedNonEmptyString,
  timeZone: TrimmedNonEmptyString,
} as const;
const OperatorAutomationWebhookTriggerFields = {
  id: OperatorAutomationTriggerId,
  kind: Schema.Literal("webhook"),
} as const;
export const OperatorAutomationTrigger = Schema.Union([
  Schema.Struct({
    ...OperatorAutomationScheduleTriggerFields,
    nextRunAt: Schema.optionalKey(IsoDateTime),
  }),
  Schema.Struct(OperatorAutomationWebhookTriggerFields),
]);
export type OperatorAutomationTrigger = typeof OperatorAutomationTrigger.Type;
const OperatorAutomationTriggerInput = Schema.Union([
  Schema.Struct(OperatorAutomationScheduleTriggerFields),
  Schema.Struct(OperatorAutomationWebhookTriggerFields),
]);
const uniqueAutomationTriggerKinds = Schema.makeFilter(
  (triggers: ReadonlyArray<{ readonly kind: "schedule" | "webhook" }>) =>
    new Set(triggers.map((trigger) => trigger.kind)).size === triggers.length ||
    "Loop trigger kinds must be unique.",
);
const OperatorAutomationTriggers = Schema.Array(OperatorAutomationTrigger).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2),
  uniqueAutomationTriggerKinds,
);
const OperatorAutomationTriggerInputs = Schema.Array(OperatorAutomationTriggerInput).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2),
  uniqueAutomationTriggerKinds,
);

export const OperatorAutomationAccess = Schema.Union([
  Schema.Struct({
    mode: Schema.Literal("inherit_workspace"),
    skills: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(64)),
  }),
  Schema.Struct({
    mode: Schema.Literal("restricted"),
    /** An empty toolset selection permits no toolsets. */
    toolsets: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(64)),
    /** Connected MCP servers allowed for this restricted run. */
    connectors: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(64)),
    skills: Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(64)),
  }),
]);
export type OperatorAutomationAccess = typeof OperatorAutomationAccess.Type;

export const LoopNotificationDelivery = Schema.Union([
  CommunicationLoopDelivery,
  Schema.Literals(["origin", "slack"]),
  EmailDeliveryDestination,
]);
export const OperatorLoopNotifications = Schema.Struct({
  delivery: Schema.optionalKey(LoopNotificationDelivery),
  creatorPrincipalId: OperatorPrincipalId,
  recipients: Schema.Array(ConversationMember).check(Schema.isMaxLength(20)),
});
export type OperatorLoopNotifications = typeof OperatorLoopNotifications.Type;
const OperatorLoopNotificationInput = Schema.Struct({
  delivery: Schema.optionalKey(LoopNotificationDelivery),
  recipients: Schema.Array(ConversationMember).check(Schema.isMaxLength(20)),
});
const OperatorLoopVisibility = Schema.Literals(["workspace", "private"]);
const OperatorLoopSharingExpectation = Schema.Struct({
  visibility: OperatorLoopVisibility,
  recipientIds: Schema.Array(OperatorPrincipalId).check(Schema.isMaxLength(20)),
});

export const OperatorAutomation = Schema.Struct({
  id: TrimmedNonEmptyString,
  projectId: OperatorProjectId,
  name: Schema.String,
  prompt: Schema.String,
  /** Assigned by the server to the person who configured the Loop's instructions. */
  memoryOwnerId: Schema.optionalKey(OperatorPrincipalId),
  memoryLearnedPrompt: Schema.optionalKey(TrimmedNonEmptyString),
  providerId: Schema.optionalKey(TrimmedNonEmptyString),
  model: Schema.optionalKey(TrimmedNonEmptyString),
  triggers: OperatorAutomationTriggers,
  access: OperatorAutomationAccess,
  enabled: Schema.Boolean,
  notifications: Schema.optionalKey(OperatorLoopNotifications),
  /** Absent on existing Loops; private access uses the creator and notification recipients. */
  visibility: Schema.optionalKey(OperatorLoopVisibility),
});
export type OperatorAutomation = typeof OperatorAutomation.Type;

export const OperatorConversationChannelBinding = Schema.Union([
  CommunicationChannelBinding,
  SlackConversationChannelBinding,
  EmailConversationChannelBinding,
]);
export type OperatorConversationChannelBinding = typeof OperatorConversationChannelBinding.Type;

export const OperatorRunTrigger = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("manual") }),
  Schema.Struct({
    kind: Schema.Literal("schedule"),
    triggerId: OperatorAutomationTriggerId,
    scheduledFor: IsoDateTime,
  }),
  Schema.Struct({
    kind: Schema.Literal("webhook"),
    triggerId: OperatorAutomationTriggerId,
    receivedAt: IsoDateTime,
    eventId: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(191))),
    payload: Schema.String.check(Schema.isMaxLength(65_536)),
  }),
]);
export type OperatorRunTrigger = typeof OperatorRunTrigger.Type;

export const OperatorAutomationRunStatus = Schema.Literals([
  "running",
  "completed",
  "failed",
  "cancelled",
]);
export type OperatorAutomationRunStatus = typeof OperatorAutomationRunStatus.Type;

export const OperatorAutomationRun = Schema.Struct({
  id: TrimmedNonEmptyString,
  loopOutcome: Schema.optionalKey(Schema.Literals(["no_change", "matched"])),
  loopEventKey: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(256))),
  conversationId: Schema.optionalKey(ConversationId),
  title: Schema.optionalKey(Schema.String),
  preview: Schema.optionalKey(Schema.String),
  startedAt: Schema.Number,
  lastActiveAt: Schema.optionalKey(Schema.Number),
  endedAt: Schema.optionalKey(Schema.Number),
  messageCount: Schema.optionalKey(NonNegativeInt),
  status: OperatorAutomationRunStatus,
});
export type OperatorAutomationRun = typeof OperatorAutomationRun.Type;

export const OperatorAutomationRunsInput = Schema.Struct({
  projectId: OperatorProjectId,
  automationId: TrimmedNonEmptyString,
  offset: Schema.optionalKey(NonNegativeInt.check(Schema.isLessThanOrEqualTo(10_000))),
  limit: Schema.optionalKey(PositiveInt.check(Schema.isLessThanOrEqualTo(50))),
});
export type OperatorAutomationRunsInput = typeof OperatorAutomationRunsInput.Type;

export const OperatorAutomationRunsResult = Schema.Struct({
  runs: Schema.Array(OperatorAutomationRun),
  hasMore: Schema.Boolean,
});
export type OperatorAutomationRunsResult = typeof OperatorAutomationRunsResult.Type;

export const OperatorAutomationRunOpenInput = Schema.Struct({
  projectId: OperatorProjectId,
  automationId: TrimmedNonEmptyString,
  runId: TrimmedNonEmptyString,
});
export type OperatorAutomationRunOpenInput = typeof OperatorAutomationRunOpenInput.Type;

export const OperatorAutomationRunOpenResult = Schema.Struct({
  conversationId: ConversationId,
});
export type OperatorAutomationRunOpenResult = typeof OperatorAutomationRunOpenResult.Type;

export const OperatorAutomationActionResult = Schema.Struct({
  automationId: Schema.optionalKey(TrimmedNonEmptyString),
  webhookUrl: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OperatorAutomationActionResult = typeof OperatorAutomationActionResult.Type;

export const OperatorLoopWebhookActionInput = Schema.Struct({
  projectId: OperatorProjectId,
  automationId: TrimmedNonEmptyString,
  triggerId: TrimmedNonEmptyString,
  action: Schema.Literals(["get", "issue", "revoke"]),
});
export type OperatorLoopWebhookActionInput = typeof OperatorLoopWebhookActionInput.Type;

export const OperatorLoopWebhookActionResult = Schema.Union([
  Schema.Struct({ configured: Schema.Literal(false) }),
  Schema.Struct({ configured: Schema.Literal(true), url: TrimmedNonEmptyString }),
  Schema.Struct({ configured: Schema.Literal(true), requiresRotation: Schema.Literal(true) }),
]);
export type OperatorLoopWebhookActionResult = typeof OperatorLoopWebhookActionResult.Type;

const OperatorAutomationScope = {
  projectId: OperatorProjectId,
} as const;

const OperatorAutomationCreateFields = {
  action: Schema.Literal("create"),
  ...OperatorAutomationScope,
  name: TrimmedNonEmptyString,
  prompt: TrimmedNonEmptyString,
  providerId: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  model: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  access: OperatorAutomationAccess,
  notifications: Schema.optionalKey(OperatorLoopNotificationInput),
  visibility: Schema.optionalKey(OperatorLoopVisibility),
} as const;

const OperatorAutomationUpdateFields = {
  action: Schema.Literal("update"),
  ...OperatorAutomationScope,
  automationId: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  prompt: TrimmedNonEmptyString,
  providerId: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  model: Schema.optionalKey(Schema.NullOr(TrimmedNonEmptyString)),
  access: OperatorAutomationAccess,
  notifications: Schema.optionalKey(OperatorLoopNotificationInput),
  visibility: Schema.optionalKey(OperatorLoopVisibility),
  expectedSharing: Schema.optionalKey(OperatorLoopSharingExpectation),
} as const;

export const OperatorAutomationActionInput = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("notifications"),
    ...OperatorAutomationScope,
    automationId: TrimmedNonEmptyString,
    notifications: OperatorLoopNotificationInput,
    expectedSharing: Schema.optionalKey(OperatorLoopSharingExpectation),
  }),
  Schema.Struct({
    ...OperatorAutomationCreateFields,
    triggers: OperatorAutomationTriggerInputs,
  }),
  Schema.Struct({
    ...OperatorAutomationUpdateFields,
    triggers: OperatorAutomationTriggerInputs,
  }),
  Schema.Struct({
    action: Schema.Literals(["pause", "resume", "remove"]),
    ...OperatorAutomationScope,
    automationId: TrimmedNonEmptyString,
  }),
]);
export type OperatorAutomationActionInput = typeof OperatorAutomationActionInput.Type;

export const OperatorProjectAppearance = Schema.Literals([
  "neutral",
  "mint",
  "sky",
  "lavender",
  "amber",
  "rose",
  "red",
  "orange",
]);
export type OperatorProjectAppearance = typeof OperatorProjectAppearance.Type;

export const OperatorProjectRuntimeState = Schema.Literals([
  "pending",
  "provisioning",
  "ready",
  "degraded",
  "failed",
  "archived",
]);
export type OperatorProjectRuntimeState = typeof OperatorProjectRuntimeState.Type;

/** Reserved release procedure, selectable in chat/Loops and hidden from managed skill lists. */
export const OPERATOR_SKILL_CREATOR = "skill-creator";
export const OPERATOR_ADD_FACT = "add-fact";

export const OperatorWorkspaceSkill = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.String,
  enabled: Schema.Boolean,
  provenance: Schema.Literals(["vendor", "user"]),
  health: Schema.Literals(["healthy", "disabled", "unavailable"]),
  version: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OperatorWorkspaceSkill = typeof OperatorWorkspaceSkill.Type;

const OperatorSkillName = TrimmedNonEmptyString.check(
  Schema.isPattern(/^[a-zA-Z0-9][a-zA-Z0-9_:-]{0,127}$/u),
);
export const OperatorSkillActionInput = Schema.Union([
  Schema.Struct({
    projectId: OperatorProjectId,
    action: Schema.Literals(["read", "remove"]),
    name: OperatorSkillName,
  }),
  Schema.Struct({
    projectId: OperatorProjectId,
    action: Schema.Literals(["create", "update"]),
    name: OperatorSkillName,
    content: TrimmedNonEmptyString.check(Schema.isMaxLength(100_000)),
  }),
  Schema.Struct({
    projectId: OperatorProjectId,
    action: Schema.Literal("toggle"),
    name: OperatorSkillName,
    enabled: Schema.Boolean,
  }),
]);
export type OperatorSkillActionInput = typeof OperatorSkillActionInput.Type;
export const OperatorSkillActionResult = Schema.Struct({
  content: Schema.optionalKey(Schema.String),
});
export type OperatorSkillActionResult = typeof OperatorSkillActionResult.Type;

export const OperatorProject = Schema.Struct({
  id: OperatorProjectId,
  name: TrimmedNonEmptyString,
  description: Schema.optionalKey(Schema.String),
  appearance: Schema.optionalKey(OperatorProjectAppearance),
  operatorName: TrimmedNonEmptyString,
  operatorPersonality: Schema.optionalKey(Schema.String),
  operatorInstructions: Schema.optionalKey(Schema.String),
  authorizationMode: Schema.Literal("single_user"),
  runtimeState: OperatorProjectRuntimeState,
  runtimeError: Schema.optionalKey(Schema.String),
  skills: Schema.optionalKey(Schema.Array(OperatorWorkspaceSkill)),
  learning: Schema.optionalKey(Schema.Array(LearningMetadata)),
  learningStatus: Schema.optionalKey(LearningStatus),
  archivedAt: Schema.optionalKey(IsoDateTime),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OperatorProject = typeof OperatorProject.Type;

const WorkspaceFactText = TrimmedNonEmptyString.check(Schema.isMaxLength(240));
export const WorkspaceFactDestination = Schema.Literals(["home", "today", "both"]);
export type WorkspaceFactDestination = typeof WorkspaceFactDestination.Type;
export const WorkspaceFactStatus = Schema.Literals(["current", "stale", "unavailable"]);
export type WorkspaceFactStatus = typeof WorkspaceFactStatus.Type;
export const WorkspaceFactSourceKind = Schema.Literals(["manual", "agent", "connector"]);
export type WorkspaceFactSourceKind = typeof WorkspaceFactSourceKind.Type;
export const WorkspaceMetricPeriod = Schema.Literals([
  "instant",
  "day",
  "week",
  "month",
  "quarter",
  "year",
]);
export type WorkspaceMetricPeriod = typeof WorkspaceMetricPeriod.Type;
export const WorkspaceFactBusinessAttention = Schema.Struct({
  ruleId: TrimmedNonEmptyString,
  operator: Schema.Literals(["above", "below"]),
  threshold: WorkspaceFactText,
  unit: WorkspaceFactText,
  metricPeriod: WorkspaceMetricPeriod,
  timezone: WorkspaceFactText,
  evaluatedAt: IsoDateTime,
});
export type WorkspaceFactBusinessAttention = typeof WorkspaceFactBusinessAttention.Type;
export const WorkspaceFact = Schema.Struct({
  id: WorkspaceFactId,
  projectId: OperatorProjectId,
  label: WorkspaceFactText,
  value: WorkspaceFactText,
  period: WorkspaceFactText,
  destination: WorkspaceFactDestination,
  status: WorkspaceFactStatus,
  sourceKind: WorkspaceFactSourceKind,
  sourceLabel: WorkspaceFactText,
  numericValue: Schema.optionalKey(WorkspaceFactText),
  unit: Schema.optionalKey(WorkspaceFactText),
  metricPeriod: Schema.optionalKey(WorkspaceMetricPeriod),
  timezone: Schema.optionalKey(WorkspaceFactText),
  sourceObservedAt: Schema.optionalKey(IsoDateTime),
  businessAttention: Schema.optionalKey(WorkspaceFactBusinessAttention),
  sourceConversationId: Schema.optionalKey(ConversationId),
  observedAt: IsoDateTime,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type WorkspaceFact = typeof WorkspaceFact.Type;

export const ConversationStatus = Schema.Literals(["active", "archived", "unavailable"]);
export type ConversationStatus = typeof ConversationStatus.Type;
export const Conversation = Schema.Struct({
  id: ConversationId,
  connectionId: HarnessConnectionId,
  projectId: OperatorProjectId,
  title: TrimmedNonEmptyString,
  preview: Schema.optionalKey(Schema.String),
  status: ConversationStatus,
  unread: Schema.Boolean,
  attentionVersion: Schema.optionalKey(TrimmedNonEmptyString),
  readVersion: Schema.optionalKey(TrimmedNonEmptyString),
  pinnedAt: Schema.optionalKey(IsoDateTime),
  audience: Schema.optionalKey(ConversationAudience),
  queuePaused: Schema.optionalKey(Schema.Boolean),
  canManageSharing: Schema.optionalKey(Schema.Boolean),
  /** Viewer-specific Home eligibility; never an access permission. */
  notifyViewer: Schema.optionalKey(Schema.Boolean),
  /** The customer/channel thread represented by this conversation, separate from its runtime. */
  channelBinding: Schema.optionalKey(OperatorConversationChannelBinding),
  externalRef: Schema.optionalKey(ExternalRef),
  historyVersion: Schema.optionalKey(TrimmedNonEmptyString),
  externalState: Schema.optionalKey(OpenSlug),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  lastActivityAt: IsoDateTime,
});
export type Conversation = typeof Conversation.Type;

export const RunStatus = Schema.Literals([
  "queued",
  "running",
  "awaiting_input",
  "stopping",
  "completed",
  "failed",
  "cancelled",
]);
export type RunStatus = typeof RunStatus.Type;
export const RunSource = Schema.Literals(["interactive", "background"]);
export type RunSource = typeof RunSource.Type;
export const Run = Schema.Struct({
  id: RunId,
  /** Reply surface for this turn; the durable conversation origin stays unchanged. */
  channelBinding: Schema.optionalKey(OperatorConversationChannelBinding),
  conversationId: ConversationId,
  automationId: Schema.optionalKey(TrimmedNonEmptyString),
  /** Recipients at submission; additions apply to future runs. */
  notificationRecipientIds: Schema.optionalKey(Schema.Array(OperatorPrincipalId)),
  /** Effective access is captured when automation execution starts. */
  access: Schema.optionalKey(OperatorAutomationAccess),
  trigger: Schema.optionalKey(OperatorRunTrigger),
  /** A completed monitor check can stay quiet without creating attention. */
  loopOutcome: Schema.optionalKey(Schema.Literals(["no_change", "matched"])),
  loopEventKey: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(256))),
  source: RunSource,
  status: RunStatus,
  externalRef: Schema.optionalKey(ExternalRef),
  externalState: Schema.optionalKey(OpenSlug),
  startedAt: Schema.optionalKey(IsoDateTime),
  summary: Schema.optionalKey(Schema.String),
  finishedAt: Schema.optionalKey(IsoDateTime),
  errorMessage: Schema.optionalKey(Schema.String),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Run = typeof Run.Type;

export const ActivityKind = Schema.Literals([
  "message",
  "tool",
  "progress",
  "status",
  "error",
  "system",
]);
export type ActivityKind = typeof ActivityKind.Type;
export const Activity = Schema.Struct({
  sequence: Schema.optionalKey(PositiveInt),
  id: ActivityId,
  conversationId: ConversationId,
  runId: Schema.optionalKey(RunId),
  kind: ActivityKind,
  actor: Schema.Literals(["user", "agent", "system", "tool"]),
  actorId: Schema.optionalKey(OperatorPrincipalId),
  actorName: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(256))),
  actorImageUrl: Schema.optionalKey(TrimmedNonEmptyString.check(Schema.isMaxLength(2_048))),
  body: Schema.String,
  attachments: Schema.optionalKey(Schema.Array(ChatAttachment)),
  files: Schema.optionalKey(Schema.Array(ChatFileAttachment)),
  externalRef: Schema.optionalKey(ExternalRef),
  createdAt: IsoDateTime,
});
export type Activity = typeof Activity.Type;

export const ApprovalKind = Schema.Literals(["command", "clarification", "sudo", "secret"]);
export type ApprovalKind = typeof ApprovalKind.Type;
export const ApprovalStatus = Schema.Literals(["pending", "approved", "rejected", "expired"]);
export type ApprovalStatus = typeof ApprovalStatus.Type;
export const OperatorApproval = Schema.Struct({
  id: OperatorApprovalId,
  conversationId: ConversationId,
  runId: RunId,
  kind: ApprovalKind,
  status: ApprovalStatus,
  title: TrimmedNonEmptyString,
  prompt: Schema.String,
  /** A secret request describes what is needed; secret values never appear here. */
  sensitive: Schema.Boolean,
  responseError: Schema.optionalKey(Schema.String),
  mcpRequest: Schema.optionalKey(
    Schema.Struct({
      mode: Schema.Literals(["form", "url"]),
      url: Schema.optionalKey(Schema.String),
      schema: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
    }),
  ),
  /** Non-sensitive answers only; secret/password responses must never enter the projection. */
  responseDraft: Schema.optionalKey(Schema.String),
  externalRef: Schema.optionalKey(ExternalRef),
  createdAt: IsoDateTime,
  expiresAt: Schema.optionalKey(IsoDateTime),
  resolvedAt: Schema.optionalKey(IsoDateTime),
  resolvedBy: Schema.optionalKey(OperatorPrincipalId),
});
export type OperatorApproval = typeof OperatorApproval.Type;

export const WorkspaceTaskStatus = Schema.Literals([
  "triage",
  "todo",
  "scheduled",
  "ready",
  "running",
  "blocked",
  "review",
  "done",
  "archived",
]);
export type WorkspaceTaskStatus = typeof WorkspaceTaskStatus.Type;

const WorkspaceTaskReminderTime = Schema.String.check(
  Schema.makeFilter((value) => {
    const parsed = DateTime.make(value);
    return Option.isSome(parsed) && DateTime.formatIso(parsed.value) === value;
  }),
);

export const WorkspaceTask = Schema.Struct({
  id: TrimmedNonEmptyString,
  projectId: OperatorProjectId,
  title: TrimmedNonEmptyString,
  detail: Schema.String,
  status: WorkspaceTaskStatus,
  work: Schema.optionalKey(
    Schema.Struct({
      objective: TrimmedNonEmptyString,
      criteria: Schema.Array(TrimmedNonEmptyString),
      steps: Schema.Array(TrimmedNonEmptyString),
      status: Schema.Literals([
        "running",
        "waiting",
        "delivering",
        "completed",
        "partial",
        "cancelled",
      ]),
      revision: TrimmedNonEmptyString,
      waitingFor: Schema.Array(TrimmedNonEmptyString),
      completedCriteria: Schema.Array(TrimmedNonEmptyString),
      brief: Schema.optionalKey(WorkBrief),
      verification: Schema.optionalKey(WorkVerification),
    }),
  ),
  createdAt: IsoDateTime,
  /** Provider-derived optimistic concurrency token for the current task request. */
  revision: Schema.optionalKey(TrimmedNonEmptyString),
  /** Current native question; null means the provider supplied no question for this revision. */
  request: Schema.optionalKey(Schema.NullOr(Schema.String)),
  result: Schema.optionalKey(Schema.String),
  /** Viewer-specific Home eligibility; shared task history remains visible. */
  notifyViewer: Schema.optionalKey(Schema.Boolean),
  /** Maito-owned reminder deadline. Snoozing only changes the viewer reminder. */
  snoozedUntil: Schema.optionalKey(WorkspaceTaskReminderTime),
  pendingAction: Schema.optionalKey(Schema.Literals(["responding", "completing"])),
  actionError: Schema.optionalKey(Schema.String),
});
export type WorkspaceTask = typeof WorkspaceTask.Type;

export const WorkspaceTaskComment = Schema.Struct({
  id: Schema.Union([Schema.String, Schema.Number]),
  /** Identifies a reply accepted through Maito's durable command path. */
  commandId: Schema.optionalKey(CommandId),
  /** Derived from the native record, not the wording. Omitted by older servers. */
  kind: Schema.optionalKey(Schema.Literals(["question", "reply", "response", "progress"])),
  author: Schema.String,
  body: Schema.String,
  attachments: Schema.optionalKey(Schema.Array(ChatAttachment)),
  createdAt: IsoDateTime,
});
export type WorkspaceTaskComment = typeof WorkspaceTaskComment.Type;

export const WorkspaceTaskDetail = Schema.Struct({
  task: WorkspaceTask,
  request: Schema.optionalKey(Schema.String),
  body: Schema.optionalKey(Schema.String),
  summary: Schema.optionalKey(Schema.String),
  result: Schema.optionalKey(Schema.String),
  comments: Schema.Array(WorkspaceTaskComment),
});
export type WorkspaceTaskDetail = typeof WorkspaceTaskDetail.Type;

export const OperatorSnapshot = Schema.Struct({
  modelVersion: Schema.Literal(2),
  snapshotSequence: PositiveInt,
  cursor: TrimmedNonEmptyString,
  observedAt: IsoDateTime,
  stale: Schema.Boolean,
  staleReason: Schema.optionalKey(Schema.String),
  canCreateProjects: Schema.optionalKey(Schema.Boolean),
  appearance: Schema.optionalKey(OperatorProjectAppearance),
  connections: Schema.Array(HarnessConnection),
  projects: Schema.Array(OperatorProject),
  conversations: Schema.Array(Conversation),
  runs: Schema.Array(Run),
  activities: Schema.Array(Activity),
  approvals: Schema.Array(OperatorApproval),
  automations: Schema.Array(OperatorAutomation),
  facts: Schema.optionalKey(Schema.Array(WorkspaceFact)),
  workspaceTasks: Schema.optionalKey(Schema.Array(WorkspaceTask)),
  activityHistory: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        conversationId: ConversationId,
        beforeActivityId: ActivityId,
      }),
    ),
  ),
});
export type OperatorSnapshot = typeof OperatorSnapshot.Type;

const entityDelta = <S extends Schema.Top>(entity: S) =>
  Schema.Struct({
    upsert: Schema.Array(entity),
    remove: Schema.Array(Schema.String),
    order: Schema.optionalKey(Schema.Array(Schema.String)),
  });
export const OperatorSnapshotDelta = Schema.Struct({
  kind: Schema.Literal("delta"),
  baseSequence: PositiveInt,
  metadata: Schema.Struct({
    modelVersion: OperatorSnapshot.fields.modelVersion,
    snapshotSequence: PositiveInt,
    cursor: TrimmedNonEmptyString,
    observedAt: IsoDateTime,
    stale: Schema.Boolean,
    staleReason: OperatorSnapshot.fields.staleReason,
    canCreateProjects: OperatorSnapshot.fields.canCreateProjects,
    appearance: OperatorSnapshot.fields.appearance,
    activityHistory: OperatorSnapshot.fields.activityHistory,
  }),
  connections: Schema.optionalKey(entityDelta(HarnessConnection)),
  projects: Schema.optionalKey(entityDelta(OperatorProject)),
  conversations: Schema.optionalKey(entityDelta(Conversation)),
  runs: Schema.optionalKey(entityDelta(Run)),
  activities: Schema.optionalKey(entityDelta(Activity)),
  approvals: Schema.optionalKey(entityDelta(OperatorApproval)),
  automations: Schema.optionalKey(entityDelta(OperatorAutomation)),
  facts: Schema.optionalKey(Schema.NullOr(entityDelta(WorkspaceFact))),
  workspaceTasks: Schema.optionalKey(Schema.NullOr(entityDelta(WorkspaceTask))),
});
export type OperatorSnapshotDelta = typeof OperatorSnapshotDelta.Type;

export const OperatorHistoryPage = Schema.Struct({
  activities: Schema.Array(Activity),
  beforeActivityId: Schema.optionalKey(ActivityId),
});
export type OperatorHistoryPage = typeof OperatorHistoryPage.Type;

const CommandEnvelope = {
  commandId: CommandId,
  createdAt: IsoDateTime,
};

export const OperatorCommand = Schema.Union([
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("workspace.appearance.update"),
    appearance: OperatorProjectAppearance,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("fact.upsert"),
    factId: WorkspaceFactId,
    projectId: OperatorProjectId,
    label: WorkspaceFactText,
    value: WorkspaceFactText,
    period: WorkspaceFactText,
    destination: WorkspaceFactDestination,
    status: WorkspaceFactStatus,
    sourceKind: WorkspaceFactSourceKind,
    sourceLabel: WorkspaceFactText,
    sourceConversationId: Schema.optionalKey(ConversationId),
    observedAt: Schema.optionalKey(IsoDateTime),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("fact.remove"),
    factId: WorkspaceFactId,
    projectId: OperatorProjectId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("project.create"),
    projectId: OperatorProjectId,
    name: TrimmedNonEmptyString,
    description: Schema.optionalKey(Schema.String),
    appearance: Schema.optionalKey(OperatorProjectAppearance),
    operatorName: Schema.optionalKey(TrimmedNonEmptyString),
    operatorPersonality: Schema.optionalKey(Schema.String),
    operatorInstructions: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("project.update"),
    projectId: OperatorProjectId,
    name: Schema.optionalKey(TrimmedNonEmptyString),
    description: Schema.optionalKey(Schema.String),
    appearance: Schema.optionalKey(OperatorProjectAppearance),
    operatorName: Schema.optionalKey(TrimmedNonEmptyString),
    operatorPersonality: Schema.optionalKey(Schema.String),
    operatorInstructions: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.create"),
    connectionId: HarnessConnectionId,
    projectId: OperatorProjectId,
    title: Schema.optionalKey(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.start"),
    connectionId: HarnessConnectionId,
    projectId: OperatorProjectId,
    title: Schema.optionalKey(TrimmedNonEmptyString),
    prompt: TrimmedNonEmptyString,
    shareWith: Schema.optionalKey(ConversationMembers),
    attachments: Schema.optionalKey(
      Schema.Array(UploadChatAttachment).check(
        Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS),
      ),
    ),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.read"),
    conversationId: ConversationId,
    attentionVersion: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.rename"),
    conversationId: ConversationId,
    title: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.archive"),
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.unarchive"),
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.delete"),
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.pin"),
    conversationId: ConversationId,
    pinned: Schema.Boolean,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.share"),
    conversationId: ConversationId,
    members: ConversationMembers,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.unshare"),
    conversationId: ConversationId,
    principalId: OperatorPrincipalId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("conversation.resume"),
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("run.submit"),
    conversationId: ConversationId,
    prompt: TrimmedNonEmptyString,
    shareWith: Schema.optionalKey(ConversationMembers),
    background: Schema.Boolean,
    attachments: Schema.optionalKey(
      Schema.Array(UploadChatAttachment).check(
        Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS),
      ),
    ),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("automation.trigger"),
    projectId: OperatorProjectId,
    automationId: TrimmedNonEmptyString,
    trigger: OperatorRunTrigger,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("run.steer"),
    runId: RunId,
    prompt: TrimmedNonEmptyString,
    attachments: Schema.optionalKey(
      Schema.Array(UploadChatAttachment).check(
        Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS),
      ),
    ),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("run.interrupt"),
    runId: RunId,
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("approval.respond"),
    approvalId: OperatorApprovalId,
    decision: Schema.Literals(["approve", "reject"]),
    response: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("workspace-task.respond"),
    projectId: OperatorProjectId,
    taskId: TrimmedNonEmptyString,
    expectedRevision: Schema.optionalKey(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    attachments: Schema.optionalKey(
      Schema.Array(UploadChatAttachment).check(
        Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS),
      ),
    ),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("workspace-task.complete"),
    projectId: OperatorProjectId,
    taskId: TrimmedNonEmptyString,
    expectedRevision: Schema.optionalKey(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    ...CommandEnvelope,
    type: Schema.Literal("workspace-task.snooze"),
    projectId: OperatorProjectId,
    taskId: TrimmedNonEmptyString,
    expectedRevision: Schema.optionalKey(TrimmedNonEmptyString),
    /** Null returns the task to Home immediately without resuming its worker. */
    until: Schema.NullOr(WorkspaceTaskReminderTime),
  }),
]);
export type OperatorCommand = typeof OperatorCommand.Type;

export const OperatorCommandReceipt = Schema.Struct({
  commandId: CommandId,
  accepted: Schema.Boolean,
  sequence: PositiveInt,
  observedAt: IsoDateTime,
  entityId: Schema.optionalKey(TrimmedNonEmptyString),
});
export type OperatorCommandReceipt = typeof OperatorCommandReceipt.Type;

export const OperatorStreamEvent = Schema.Struct({
  kind: Schema.Literal("event"),
  sequence: PositiveInt,
  cursor: TrimmedNonEmptyString,
  eventType: OpenSlug,
  entityType: Schema.Literals([
    "connection",
    "project",
    "conversation",
    "run",
    "activity",
    "approval",
    "automation",
    "fact",
    "workspace_task",
  ]),
  entityId: TrimmedNonEmptyString,
  observedAt: IsoDateTime,
});
export type OperatorStreamEvent = typeof OperatorStreamEvent.Type;
export const OperatorStreamItem = Schema.Union([
  OperatorSnapshotDelta,
  Schema.Struct({ kind: Schema.Literal("snapshot"), snapshot: OperatorSnapshot }),
  OperatorStreamEvent,
  Schema.Struct({
    kind: Schema.Literal("synchronized"),
    sequence: PositiveInt,
    observedAt: IsoDateTime,
  }),
]);
export type OperatorStreamItem = typeof OperatorStreamItem.Type;

export class HarnessConnectionError extends Schema.TaggedErrorClass<HarnessConnectionError>()(
  "HarnessConnectionError",
  {
    connectionId: Schema.optionalKey(HarnessConnectionId),
    reason: Schema.Literals([
      "not_found",
      "disconnected",
      "unavailable",
      "authentication_required",
      "invalid_configuration",
      "invalid_response",
      "internal_error",
    ]),
    message: Schema.String,
  },
) {}

export class UnsupportedHarnessCapabilityError extends Schema.TaggedErrorClass<UnsupportedHarnessCapabilityError>()(
  "UnsupportedHarnessCapabilityError",
  {
    connectionId: HarnessConnectionId,
    capability: OpenSlug,
    message: Schema.String,
  },
) {}

export class OperatorEntityNotFoundError extends Schema.TaggedErrorClass<OperatorEntityNotFoundError>()(
  "OperatorEntityNotFoundError",
  {
    entityType: OpenSlug,
    entityId: TrimmedNonEmptyString,
    message: Schema.String,
  },
) {}

export class OperatorCommandRejectedError extends Schema.TaggedErrorClass<OperatorCommandRejectedError>()(
  "OperatorCommandRejectedError",
  {
    commandId: CommandId,
    reason: Schema.Literals(["conflict", "invalid_source", "invalid_state"]),
    message: Schema.String,
  },
) {}

export class WorkspaceAccessDeniedError extends Schema.TaggedErrorClass<WorkspaceAccessDeniedError>()(
  "WorkspaceAccessDeniedError",
  {
    projectId: OperatorProjectId,
    action: OpenSlug,
    message: Schema.String,
  },
) {}

export const OperatorRpcError = Schema.Union([
  HarnessConnectionError,
  UnsupportedHarnessCapabilityError,
  OperatorEntityNotFoundError,
  OperatorCommandRejectedError,
  WorkspaceAccessDeniedError,
  OperatorVaultError,
]);
export type OperatorRpcError = typeof OperatorRpcError.Type;

export const OPERATOR_WS_METHODS = {
  queryLogs: "operator.queryLogs",
  exportLogs: "operator.exportLogs",
  getLogPolicy: "operator.getLogPolicy",
  setLogPolicy: "operator.setLogPolicy",
  getSnapshot: "operator.getSnapshot",
  openConversation: "operator.openConversation",
  getConversationHistory: "operator.getConversationHistory",
  getWorkspaceTask: "operator.getWorkspaceTask",
  listConnections: "operator.listConnections",
  sourceControlAction: "operator.sourceControlAction",
  connectionAction: "operator.connectionAction",
  workspaceMcpConsent: "operator.workspaceMcpConsent",
  skillAction: "operator.skillAction",
  environmentAction: "operator.environmentAction",
  knowledgeAction: "operator.knowledgeAction",
  memoryAction: "operator.memoryAction",
  getProviderConfiguration: "operator.getProviderConfiguration",
  saveProviderConfiguration: "operator.saveProviderConfiguration",
  activateProvider: "operator.activateProvider",
  setProviderReasoningEffort: "operator.setProviderReasoningEffort",
  disconnectProvider: "operator.disconnectProvider",
  providerOAuthAction: "operator.providerOAuthAction",
  listAutomations: "operator.listAutomations",
  listAutomationRuns: "operator.listAutomationRuns",
  openAutomationRun: "operator.openAutomationRun",
  automationAction: "operator.automationAction",
  loopWebhookAction: "operator.loopWebhookAction",
  communicationConnections: "operator.communicationConnections",
  getEmailSetup: "operator.getEmailSetup",
  emailAction: "operator.emailAction",
  getSlackSetup: "operator.getSlackSetup",
  slackAction: "operator.slackAction",
  listVaultSecrets: "operator.listVaultSecrets",
  upsertVaultSecret: "operator.upsertVaultSecret",
  deleteVaultSecret: "operator.deleteVaultSecret",
  reconnect: "operator.reconnect",
  dispatchCommand: "operator.dispatchCommand",
  subscribe: "operator.subscribe",
} as const;

/** One recipient set for Home and Slack; the creator is always included. */
export const loopNotificationRecipientIds = (loop: OperatorAutomation) =>
  loop.notifications === undefined
    ? []
    : [
        loop.notifications.creatorPrincipalId,
        ...loop.notifications.recipients.map((member) => member.principalId),
      ];

/** Removing a recipient takes effect even for work that was already queued. */
export const isLoopNotificationRecipient = (
  loop: OperatorAutomation | undefined,
  run: Run,
  principalId: OperatorPrincipalId,
) =>
  loop !== undefined &&
  run.notificationRecipientIds?.includes(principalId) === true &&
  (loop.notifications?.creatorPrincipalId === principalId ||
    loop.notifications?.recipients.some((member) => member.principalId === principalId) === true);
